import { effectSchemaToZod } from "@redotech/effect-api-shared-common/zod";
import { AiActivityNotification } from "@redotech/effect-api-shared/models/shopper-ai/ai-activity-notification";
import {
  widgetConversationsEventSchema,
  widgetConversationsStreamInitialSchema,
  widgetConversationsStreamInputSchema,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/widget-conversation-stream";
import { SupportTicketBulkActionNotification } from "@redotech/effect-api-shared/models/support-ticket/support-ticket-bulk-action-notification";
import { SupportTicketNotification } from "@redotech/effect-api-shared/models/support-ticket/support-ticket-notification";
import { UserNotification } from "@redotech/effect-api-shared/models/team/user-notification";
import * as z from "zod";

export interface StreamChannelSchemaDefinition<TInput, TInitial, TEvent> {
  readonly inputSchema: z.ZodType<TInput>;
  readonly initialSchema: z.ZodType<TInitial>;
  readonly eventSchema: z.ZodType<TEvent>;
}

export function defineStreamChannelSchema<TInput, TInitial, TEvent>(
  definition: StreamChannelSchemaDefinition<TInput, TInitial, TEvent>,
): StreamChannelSchemaDefinition<TInput, TInitial, TEvent> {
  return definition;
}

export type AnyStreamChannelSchemaDefinition = StreamChannelSchemaDefinition<
  unknown,
  unknown,
  unknown
>;

export const supportTicketNotificationsStreamInputSchema = z.object({});
export type SupportTicketNotificationsStreamInput = z.infer<
  typeof supportTicketNotificationsStreamInputSchema
>;

export const userNotificationsStreamInputSchema = z.object({});
export type UserNotificationsStreamInput = z.infer<
  typeof userNotificationsStreamInputSchema
>;

export const supportTicketNotificationsStreamChannelSchema =
  defineStreamChannelSchema({
    eventSchema: effectSchemaToZod(SupportTicketNotification),
    initialSchema: z.undefined(),
    inputSchema: supportTicketNotificationsStreamInputSchema,
  });

export const userNotificationsStreamChannelSchema = defineStreamChannelSchema({
  eventSchema: effectSchemaToZod(UserNotification),
  initialSchema: z.undefined(),
  inputSchema: userNotificationsStreamInputSchema,
});

export const supportTicketBulkActionsStreamInputSchema = z.object({});
export type SupportTicketBulkActionsStreamInput = z.infer<
  typeof supportTicketBulkActionsStreamInputSchema
>;

export const supportTicketBulkActionsStreamChannelSchema =
  defineStreamChannelSchema({
    eventSchema: effectSchemaToZod(SupportTicketBulkActionNotification),
    initialSchema: z.undefined(),
    inputSchema: supportTicketBulkActionsStreamInputSchema,
  });

export const presenceStreamInputSchema = z.object({});
export type PresenceStreamInput = z.infer<typeof presenceStreamInputSchema>;

export const presenceViewingEventSchema = z.object({
  _tag: z.literal("presenceViewing"),
  supportTicketId: z.string(),
  userId: z.string(),
});

export const presenceClearedEventSchema = z.object({
  _tag: z.literal("presenceCleared"),
  supportTicketId: z.string(),
  userId: z.string(),
  activity: z.enum(["viewing", "typing", "all"]),
});

export const typingEventSchema = z.object({
  _tag: z.literal("typing"),
  supportTicketId: z.string(),
  userId: z.string(),
});

export const presenceStreamEventSchema = z.discriminatedUnion("_tag", [
  presenceViewingEventSchema,
  presenceClearedEventSchema,
  typingEventSchema,
]);
export type PresenceStreamEvent = z.infer<typeof presenceStreamEventSchema>;

export const presenceStreamInitialSchema = z.array(presenceViewingEventSchema);
export type PresenceStreamInitial = z.infer<typeof presenceStreamInitialSchema>;

export const presenceStreamChannelSchema = defineStreamChannelSchema({
  eventSchema: presenceStreamEventSchema,
  initialSchema: presenceStreamInitialSchema,
  inputSchema: presenceStreamInputSchema,
});

export const supportTicketAiActivityStreamInputSchema = z.object({});
export type SupportTicketAiActivityStreamInput = z.infer<
  typeof supportTicketAiActivityStreamInputSchema
>;

export const supportTicketAiActivityStreamChannelSchema =
  defineStreamChannelSchema({
    eventSchema: effectSchemaToZod(AiActivityNotification),
    initialSchema: z.undefined(),
    inputSchema: supportTicketAiActivityStreamInputSchema,
  });

export const widgetConversationsStreamChannelSchema = defineStreamChannelSchema(
  {
    eventSchema: widgetConversationsEventSchema,
    initialSchema: widgetConversationsStreamInitialSchema,
    inputSchema: widgetConversationsStreamInputSchema,
  },
);
